// Life & Wealth Solutions — Phoenix Lead Scraper Control Panel
// PRA INTERNAL — john@phoenixrescueapps.online, teresa@, shawn@ only

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const PRA_EMAILS = new Set([
  'john@phoenixrescueapps.online',
  'teresa@lifeandwealthsolutions.online',
  'shawn@lifeandwealthsolutions.online'
]);

const API         = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const SCRAPER_URL = process.env.REACT_APP_SCRAPER_URL || 'http://localhost:3099';

const ALL_STATES = [
  'Georgia','Florida','Texas','New York','California','Pennsylvania','New Jersey',
  'North Carolina','Virginia','Michigan','Ohio','Illinois','Arizona','Tennessee',
  'Colorado','Washington','Massachusetts','Maryland','Indiana','Wisconsin',
  'South Carolina','Alabama','Louisiana','Minnesota','Missouri','Connecticut',
  'Nevada','Oregon','Arkansas','Mississippi','Iowa','Kansas','Nebraska',
  'Utah','New Mexico','Idaho','Montana','Delaware','Rhode Island'
];

const WEEK_PRESETS: Record<string, string[]> = {
  'Week 1 — Southeast': ['Georgia','Florida','Texas','North Carolina','South Carolina','Tennessee','Alabama','Mississippi','Louisiana','Arkansas'],
  'Week 2 — Northeast': ['New York','Pennsylvania','New Jersey','Massachusetts','Connecticut','Virginia','Maryland','Delaware','Rhode Island'],
  'Week 3 — Midwest':   ['Illinois','Ohio','Michigan','Indiana','Wisconsin','Minnesota','Missouri','Iowa','Kansas','Nebraska'],
  'Week 4 — West':      ['California','Arizona','Nevada','Colorado','Washington','Oregon','Utah','New Mexico','Idaho','Montana'],
};

interface ScraperInfo {
  scraper_status: string;
  last_updated:   string | null;
  last_run_data:  any;
  queue_counts:   Record<string, number>;
  pending_whales: number;
}

const ScraperControl: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo]               = useState<ScraperInfo | null>(null);
  const [mode, setMode]               = useState<'auto'|'manual'>('manual');
  const [selectedStates, setSelected] = useState<string[]>(['Georgia','Florida']);
  const [running, setRunning]         = useState(false);
  const [toast, setToast]             = useState('');
  const [loading, setLoading]         = useState(true);
  const [authorized, setAuthorized]   = useState(false);

  const token   = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const scraperHeaders = { 'X-Scraper-Token': process.env.REACT_APP_SCRAPER_TOKEN || '', 'Content-Type': 'application/json' };

  // Authorization check — must happen before early return but after hooks
  useEffect(() => {
    if (user?.email && PRA_EMAILS.has(user.email)) {
      setAuthorized(true);
    }
  }, [user]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const fetchInfo = useCallback(async () => {
    if (!user?.email || !PRA_EMAILS.has(user.email)) return;
    try {
      const res  = await fetch(`${API}/scraper-leads/scraper-info`, { headers });
      const data = await res.json();
      setInfo(data);
      setLoading(false);
    } catch { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!authorized) return;
    fetchInfo();
    const t = setInterval(fetchInfo, 15000);
    return () => clearInterval(t);
  }, [authorized, fetchInfo]);

  // Access guard rendered AFTER hooks
  if (!user?.email || !PRA_EMAILS.has(user.email)) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900">Access Restricted</p>
          <p className="text-sm text-gray-500 mt-1">Phoenix Rescue Apps internal only.</p>
        </div>
      </div>
    );
  }

  const toggleMode = async (newMode: 'auto'|'manual') => {
    try {
      await fetch(`${SCRAPER_URL}/mode`, {
        method: 'POST', headers: scraperHeaders,
        body: JSON.stringify({ mode: newMode })
      });
      setMode(newMode);
      showToast(`Scraper set to ${newMode.toUpperCase()} mode`);
    } catch { showToast('Could not reach scraper service — check that it is running'); }
  };

  const triggerScrape = async () => {
    if (running) return;
    if (!selectedStates.length) { showToast('Select at least one state'); return; }
    setRunning(true);
    showToast('Scrape triggered — leads will appear in the queue when complete');
    try {
      await fetch(`${SCRAPER_URL}/trigger`, {
        method: 'POST', headers: scraperHeaders,
        body: JSON.stringify({ states: selectedStates })
      });
    } catch { showToast('Could not reach scraper service — check that it is running'); }
    finally { setTimeout(() => { setRunning(false); fetchInfo(); }, 3000); }
  };

  const toggleState = (s: string) => {
    setSelected(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const statusColor = info?.scraper_status === 'running' ? 'text-blue-600 bg-blue-100' :
                      info?.scraper_status === 'error'   ? 'text-red-600 bg-red-100'   :
                      'text-green-600 bg-green-100';

  const lastRun = info?.last_run_data?.last_run;

  return (
    <div className="space-y-6 pb-10 max-w-4xl">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white px-4 py-3 rounded-xl shadow-xl text-sm max-w-sm">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Scraper Control Panel</h1>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">PRA INTERNAL</span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">Phoenix Lead Scraper — Insurance Buyer Edition</p>
        </div>
        <button onClick={() => navigate('/scraper-queue')}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          View Queue ({info?.queue_counts?.pending || 0} pending)
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Scraper Status</h3>
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${statusColor}`}>
              {loading ? 'Loading...' : (info?.scraper_status || 'idle')}
            </span>
            {info?.scraper_status === 'running' && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />}
          </div>
          {info?.last_updated && (
            <p className="text-xs text-gray-400">Last updated: {new Date(info.last_updated).toLocaleTimeString()}</p>
          )}
          {lastRun && (
            <div className="mt-3 space-y-1 text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
              <p><span className="font-medium">Last run:</span> {lastRun.completed_at ? new Date(lastRun.completed_at).toLocaleString() : '—'}</p>
              <p><span className="font-medium">Total leads:</span> {lastRun.total_leads || 0}</p>
              <p><span className="font-medium">Whales found:</span> {lastRun.whale_leads || 0} 🐋</p>
              <p><span className="font-medium">Pushed to queue:</span> {lastRun.pushed_to_lws || 0}</p>
              <p><span className="font-medium">Duration:</span> {lastRun.duration_seconds || 0}s</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Auto / Manual Toggle</h3>
          <div className="flex gap-3 mb-4">
            <button onClick={() => toggleMode('auto')}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-colors ${mode === 'auto' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              AUTO
              <p className="text-xs font-normal mt-0.5 opacity-80">2 AM daily, rotating states</p>
            </button>
            <button onClick={() => toggleMode('manual')}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-colors ${mode === 'manual' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              MANUAL
              <p className="text-xs font-normal mt-0.5 opacity-80">You trigger each run</p>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {[
              { label: 'Pending', value: info?.queue_counts?.pending || 0, color: 'text-blue-600' },
              { label: 'Approved', value: info?.queue_counts?.approved || 0, color: 'text-green-600' },
              { label: 'Whales', value: info?.pending_whales || 0, color: 'text-yellow-600' },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg p-2">
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                <p className="text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Manual Run — Select States</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(WEEK_PRESETS).map(([label, states]) => (
            <button key={label} onClick={() => setSelected(states)}
              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">{label}</button>
          ))}
          <button onClick={() => setSelected(ALL_STATES)}
            className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">All States</button>
          <button onClick={() => setSelected([])}
            className="px-3 py-1.5 text-xs bg-gray-50 text-gray-500 rounded-lg hover:bg-gray-100">Clear</button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 mb-5">
          {ALL_STATES.map(state => (
            <button key={state} onClick={() => toggleState(state)}
              className={`px-2 py-1.5 text-xs rounded-lg text-left transition-colors truncate ${
                selectedStates.includes(state) ? 'bg-blue-600 text-white font-medium' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}>{state}</button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <button onClick={triggerScrape} disabled={running || !selectedStates.length}
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {running ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Running...</>
            ) : (
              <>🚀 Run Scrape Now ({selectedStates.length} state{selectedStates.length !== 1 ? 's' : ''})</>
            )}
          </button>
          <p className="text-xs text-gray-400">Leads go to review queue first — you approve before entering main pipeline.</p>
        </div>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-orange-800">This panel is visible only to: John, Teresa, and Shawn. Not accessible to demo participants or platform purchasers.</p>
      </div>
    </div>
  );
};

export default ScraperControl;
